import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Target, CheckCircle, Download, Moon, Sun, Sparkles, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../components/ThemeProvider';
import { useAuth } from '../contexts/AuthContext';

export default function LandingPage() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const howItWorksRef = useRef(null);

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleStartAnalysis = () => {
    if (!isAuthenticated) {
      navigate('/login');
    } else if (user?.analyses_remaining === 0) {
      navigate('/payment');
    } else {
      navigate('/analyze');
    }
  };

  const features = [
    {
      icon: <Target className="w-6 h-6" />,
      title: 'ATS Compatibility Score',
      description: 'Get a precise 0-100 score based on keyword match, role relevance, and formatting.'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Prioritized Fixes',
      description: 'Receive actionable feedback ranked by impact: Critical, High, Medium, Low.'
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: 'AI-Powered Rewrites',
      description: 'Get ATS-friendly rewrites of your summary, skills, and experience bullets.'
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: 'Export Ready',
      description: 'Download your optimized resume in plain text or ATS-friendly PDF format.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 noise-overlay">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-8 h-8 text-slate-700 dark:text-slate-300" />
            <span className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Resume Rx
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <Button
                  data-testid="dashboard-button"
                  onClick={() => navigate('/analyze')}
                  variant="outline"
                  className="rounded-full"
                >
                  Dashboard
                </Button>
                <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-full px-3 py-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                  {user?.analyses_remaining || 0} left
                </div>
              </>
            ) : (
              <Button
                data-testid="login-button-header"
                onClick={() => navigate('/login')}
                className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-full px-6"
              >
                Login
              </Button>
            )}
            
            <Button
              data-testid="theme-toggle-button"
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className="rounded-full"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:col-span-7"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white mb-6 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Your Resume Doctor for <span className="text-emerald-500">ATS Success</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
                Beat the robots, impress recruiters. Get instant ATS compatibility scores, 
                prioritized fixes, and AI-powered rewrites that land interviews.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="start-analysis-button"
                  onClick={handleStartAnalysis}
                  className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 shadow-lg hover:shadow-xl rounded-full px-8 py-6 text-lg font-semibold"
                >
                  {isAuthenticated ? 'Analyze My Resume' : 'Login to Start'}
                </Button>
                <Button
                  data-testid="learn-more-button"
                  onClick={scrollToHowItWorks}
                  variant="outline"
                  className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border-2 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-full px-8 py-6 text-lg font-medium"
                >
                  How It Works
                </Button>
              </div>
              
              {/* Charity Message */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mt-8 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400"
              >
                <Heart className="w-5 h-5 text-emerald-500 fill-emerald-500" />
                <span>100% of proceeds support therapy programs for autistic children</span>
              </motion.div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="md:col-span-5 float-animation"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-emerald-500/20 dark:bg-emerald-500/10 rounded-3xl blur-3xl"></div>
                <img
                  src="https://images.unsplash.com/photo-1511385348-a52b4a160dc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwd29ya3NwYWNlJTIwbGFwdG9wJTIwY2xlYW4lMjBkZXNrfGVufDB8fHx8MTc3MTA2ODk2NXww&ixlib=rb-4.1.0&q=85"
                  alt="Minimalist workspace"
                  className="relative rounded-3xl shadow-2xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section ref={howItWorksRef} className="py-24 px-6 md:px-12 lg:px-24 bg-slate-50 dark:bg-slate-950" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              How It Works
            </h2>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400">
              Get your resume ATS-ready in 3 simple steps
            </p>
          </motion.div>

          <div className="space-y-8">
            {[
              {
                step: '1',
                title: 'Upload or Paste Your Resume',
                description: 'Upload a PDF file or paste your resume text directly. We support both formats for your convenience.'
              },
              {
                step: '2',
                title: 'Add the Target Job Description',
                description: 'Paste the job posting you\'re applying for. Our AI will analyze how well your resume matches the role requirements.'
              },
              {
                step: '3',
                title: 'Get Your Analysis & Optimize',
                description: 'Receive your ATS score (0-100), prioritized fixes, keyword gaps, and AI-rewritten sections. Export your optimized resume in TXT or PDF format.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="flex gap-6 items-start"
                data-testid={`how-it-works-step-${index}`}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {item.step}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Button
              data-testid="how-it-works-cta"
              onClick={handleStartAnalysis}
              className="bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg hover:shadow-xl rounded-full px-10 py-6 text-lg font-semibold"
            >
              {isAuthenticated ? 'Get Started Now' : 'Login to Get Started'}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Why Resume Rx?
            </h2>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Built by recruiters, powered by AI. Every feature designed to get you past ATS filters and into interviews.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-8 hover:shadow-lg transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-emerald-500 text-white rounded-xl flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Ready to optimize your resume?
            </h2>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 mb-8">
              Get instant feedback and land more interviews. Start your free analysis now.
            </p>
            <Button
              data-testid="cta-start-button"
              onClick={handleStartAnalysis}
              className="bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg hover:shadow-xl rounded-full px-12 py-6 text-lg font-semibold"
            >
              {isAuthenticated ? 'Start Analysis' : 'Login to Start'}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 lg:px-24 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          {/* Charity Banner */}
          <div className="mb-6 flex items-center justify-center gap-3 text-center bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
            <Heart className="w-6 h-6 text-emerald-500 fill-emerald-500 flex-shrink-0" />
            <p className="text-sm sm:text-base text-emerald-800 dark:text-emerald-300 font-medium">
              100% of proceeds support therapy programs for autistic children
            </p>
          </div>
          
          <div className="text-center text-slate-600 dark:text-slate-400 text-sm space-y-2">
            <div className="flex items-center justify-center gap-4 mb-3">
              {!isAuthenticated && (
                <button
                  onClick={() => navigate('/login')}
                  className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
                  data-testid="footer-login-button"
                >
                  Login
                </button>
              )}
              <a href="#" onClick={(e) => { e.preventDefault(); scrollToHowItWorks(); }} className="hover:underline">
                How It Works
              </a>
              <a href="#" className="hover:underline">
                Contact
              </a>
            </div>
            <p>&copy; 2025 Resume Rx. Built with precision and care.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
